import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import pinCodeIcon from '@crm/assets/icons8-pin-code.png';
import ModalDialog from '@shared/common/modals/ModalDialog.jsx';

import {
  crmApi,
  duplicateIdentityConflict,
  duplicatePhoneWarning,
  errCode,
  errMsg,
} from '@crm/api';
import { clientsActions, clientsSelectors } from '@crm/features/clients/clientsSlice';
import { ClientStatusSelect } from '@crm/components/ClientStatusSelect';
import { TrialDateModal } from '@crm/components/TrialDateModal';
import { ClientsNewClientModal } from '@crm/pages/ClientsNewClientModal';
import { hasCrmPermission } from '@crm/permissions';
import {
  describeTrialDate,
  formatTrialDateShort,
  localIsoToday,
  TRIAL_DATE_CHANGE_TYPE_LABELS,
  TRIAL_DATE_ERROR_LABELS,
} from '@crm/utils/trialDate';
import {
  UiAutosuggest,
  UiButton,
  UiCheckbox,
  UiDateRangePicker,
  UiInput,
  UiLinkButton,
  UiSelect,
  UiTextarea,
} from '@shared/common/ui';
import {
  formatRuMobilePhone,
  normalizeRuMobilePhone,
  RU_MOBILE_FORMAT_CHARS,
  RU_MOBILE_MASK,
} from '@shared/utils/ruMobilePhone';

function dayStartUtcIso(ymd) {
  return `${ymd}T00:00:00.000Z`;
}

function dayEndExclusiveUtcIso(ymd) {
  const dt = new Date(`${ymd}T00:00:00.000Z`);
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString();
}

function fmtDateTime(v) {
  return v ? v.slice(0, 16).replace('T', ' ') : '—';
}

function fmtPaymentDateTime(v) {
  if (!v) return '—';
  const match = String(v).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return fmtDateTime(v);
  return `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}`;
}

const RELATIVE_RELATIONSHIP_OPTIONS = [
  { value: 'родитель', label: 'Родитель' },
  { value: 'ребёнок', label: 'Ребёнок' },
  { value: 'бабушка/дедушка', label: 'Бабушка/дедушка' },
  { value: 'внук/внучка', label: 'Внук/внучка' },
  { value: 'брат/сестра', label: 'Брат/сестра' },
  { value: 'супруг(а)', label: 'Супруг(а)' },
  { value: 'опекун', label: 'Опекун' },
];

const MARKETING_FIELD_LABELS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmTerm: 'utm_term',
  landing: 'landing',
  referrer: 'referrer',
};

function flattenMarketingItems(items) {
  const rows = [];
  for (const item of items ?? []) {
    for (const [field, label] of Object.entries(MARKETING_FIELD_LABELS)) {
      const value = item[field];
      if (value == null || value === '') continue;
      rows.push({
        id: `${item.id}:${field}`,
        dateTime: item.dateTime,
        key: label,
        value: String(value),
      });
    }
    const params = item.params && typeof item.params === 'object' ? item.params : {};
    for (const [key, value] of Object.entries(params)) {
      if (value == null || value === '') continue;
      rows.push({
        id: `${item.id}:param:${key}`,
        dateTime: item.dateTime,
        key,
        value: String(value),
      });
    }
  }
  return rows;
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function currentMonthRange() {
  const now = new Date();
  return {
    from: toYmd(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toYmd(now),
  };
}

const GENDER_OPTIONS = [
  { value: '1', label: 'Мужской' },
  { value: '2', label: 'Женский' },
];

const DISCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'Без скидки' },
  { value: '1', label: 'Процентная (%)' },
  { value: '2', label: 'Фиксированная (₽)' },
];

const SALE_STATUS_LABELS = {
  0: 'Отмена',
  1: 'В ожидании',
  2: 'Оплачен',
  3: 'Возврат',
};

const SALE_PAYMENT_TYPE_LABELS = {
  1: 'Безналичный',
  2: 'Наличный',
  3: 'Подарок',
};

const PAYMENT_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Все' },
  { value: '1', label: 'В ожидании' },
  { value: '2', label: 'Оплачен' },
  { value: '3', label: 'Возврат' },
  { value: '0', label: 'Отмена' },
];

const PAYMENT_ROW_LEGEND = [
  { key: 'cashless', label: 'Оплачен — безналичный' },
  { key: 'cash', label: 'Оплачен — наличный' },
  { key: 'gift', label: 'Оплачен — подарок' },
  { key: 'pending', label: 'В ожидании' },
  { key: 'canceled', label: 'Отмена' },
  { key: 'refund', label: 'Возврат' },
];

function fmtSaleItemDiscount(it) {
  if (it.saleAmount == null || Number(it.saleAmount) === 0) return '—';
  const u = it.saleType === 1 ? '%' : '₽';
  const extra =
    it.saleType === 1 && it.saleAmountByPrice != null
      ? ` (${Number(it.saleAmountByPrice).toLocaleString('ru-RU')} ₽)`
      : '';
  return `${Number(it.saleAmount).toLocaleString('ru-RU')}${u}${extra}`;
}

export function ClientDetailPage() {
  const { ownerId, branchId, clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isNew = clientId === 'new';
  const backTo = `/${ownerId}/crm/${branchId}/clients${location.search}`;

  const saveStatus = useSelector(clientsSelectors.selectSaveStatus(branchId));
  const saveError = useSelector(clientsSelectors.selectSaveError(branchId));
  const { profile, branches: crmBranches = [] } = useSelector((s) => s.crmAuth);
  const canCreateClient = hasCrmPermission(profile, crmBranches, branchId, 'clients.create');
  const canViewMain = hasCrmPermission(profile, crmBranches, branchId, 'client.main.view');
  const canEditMain = hasCrmPermission(profile, crmBranches, branchId, 'client.main.edit');
  const canUpdateStatus = hasCrmPermission(profile, crmBranches, branchId, 'clients.status.update');
  const canManageContacts = hasCrmPermission(profile, crmBranches, branchId, 'client.contacts.manage');
  const canManageDiscount = hasCrmPermission(profile, crmBranches, branchId, 'client.discount.manage');
  const canManageDirections = hasCrmPermission(profile, crmBranches, branchId, 'client.directions.manage');
  const canManageRelatives = hasCrmPermission(profile, crmBranches, branchId, 'client.relatives.manage');
  const canManageBranches = hasCrmPermission(profile, crmBranches, branchId, 'client.branches.manage');
  const canViewPayments = hasCrmPermission(profile, crmBranches, branchId, 'client.payments.view');
  const canManagePaymentStatus = hasCrmPermission(
    profile,
    crmBranches,
    branchId,
    'client.payments.status.manage',
  );
  const canViewPaymentCheque = hasCrmPermission(
    profile,
    crmBranches,
    branchId,
    'client.payments.cheque.view',
  );
  const canViewPaymentItems = hasCrmPermission(
    profile,
    crmBranches,
    branchId,
    'client.payments.items.view',
  );
  const canViewVisitings = hasCrmPermission(profile, crmBranches, branchId, 'client.visitings.view');
  const canViewHistory = hasCrmPermission(profile, crmBranches, branchId, 'client.history.view');
  const canViewMarketing = hasCrmPermission(profile, crmBranches, branchId, 'client.marketing.view');

  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(!isNew);
  const [tab, setTab] = useState('main');
  const [statuses, setStatuses] = useState([]);
  const [directionOptions, setDirectionOptions] = useState([]);

  const [mainForm, setMainForm] = useState({
    fio: '',
    phone: '',
    gender: '1',
    birthday: '',
    comment: '',
    status: '',
    paykeeperSkipCash: false,
  });
  const [saveMainLoading, setSaveMainLoading] = useState(false);
  const [saveMainError, setSaveMainError] = useState(null);
  const [newClientSaving, setNewClientSaving] = useState(false);
  const [newClientError, setNewClientError] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [creatingDuplicate, setCreatingDuplicate] = useState(false);
  const [attachError, setAttachError] = useState(null);

  const [discountForm, setDiscountForm] = useState({ saleType: '', saleAmount: '' });

  const [directionDraftId, setDirectionDraftId] = useState('');
  const [directionDraftLabel, setDirectionDraftLabel] = useState('');
  const [directionQuery, setDirectionQuery] = useState('');
  const [directionSuggestions, setDirectionSuggestions] = useState([]);
  const [directionSaving, setDirectionSaving] = useState(false);
  const [directionError, setDirectionError] = useState(null);
  // Открытый запрос даты пробного: { direction, status, mode }.
  const [trialRequest, setTrialRequest] = useState(null);
  const [trialError, setTrialError] = useState(null);
  const [trialHistory, setTrialHistory] = useState({
    clientDirectionId: null,
    items: [],
    loading: false,
    error: null,
  });
  const today = useMemo(() => localIsoToday(), []);

  const [relQuery, setRelQuery] = useState('');
  const [relOptions, setRelOptions] = useState([]);
  const [relDraft, setRelDraft] = useState(null);
  const [relRelationship, setRelRelationship] = useState('родитель');
  const [relSaving, setRelSaving] = useState(false);
  const [relError, setRelError] = useState(null);
  const [relCreateOpen, setRelCreateOpen] = useState(false);
  const [branchDraftId, setBranchDraftId] = useState('');
  const [branchSaving, setBranchSaving] = useState(false);
  const [branchError, setBranchError] = useState(null);

  const [payments, setPayments] = useState({ items: [], loading: false, error: null });
  const [history, setHistory] = useState({ items: [], loading: false, error: null });
  const [marketing, setMarketing] = useState({ items: [], loading: false, error: null });
  const [visitings, setVisitings] = useState({ items: [], loading: false, error: null });
  const [paymentsFilter, setPaymentsFilter] = useState({ from: '', to: '' });
  const [paymentsStatusFilter, setPaymentsStatusFilter] = useState('');
  const [paymentActionError, setPaymentActionError] = useState(null);
  const [paymentStatusBusyId, setPaymentStatusBusyId] = useState(null);
  const [paymentChequeBusyId, setPaymentChequeBusyId] = useState(null);
  const [paymentCodeBusyId, setPaymentCodeBusyId] = useState(null);
  const [paymentCodeModal, setPaymentCodeModal] = useState(null);
  const [saleModal, setSaleModal] = useState({
    open: false,
    saleId: null,
    data: null,
    loading: false,
    error: null,
  });
  const [historyFilter, setHistoryFilter] = useState({ from: '', to: '', q: '' });
  const [marketingFilter, setMarketingFilter] = useState({ from: '', to: '', q: '' });
  const [visitingsFilter, setVisitingsFilter] = useState(() => currentMonthRange());

  useEffect(() => {
    dispatch(clientsActions.resetSave(branchId));
  }, [dispatch, branchId]);

  const loadDetail = useCallback(async () => {
    if (isNew) {
      setDetail(null);
      setLoadingDetail(false);
      return;
    }
    setLoadingDetail(true);
    try {
      const { data } = await crmApi(branchId).get(`/clients/${clientId}`);
      setDetail(data.client ?? null);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [branchId, clientId, isNew]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    const visible =
      (tab === 'main' && canViewMain) ||
      (tab === 'payments' && canViewPayments) ||
      (tab === 'visitings' && canViewVisitings) ||
      (tab === 'history' && canViewHistory) ||
      (tab === 'marketing' && canViewMarketing);
    if (!visible) {
      if (canViewMain) setTab('main');
      else if (canViewPayments) setTab('payments');
      else if (canViewVisitings) setTab('visitings');
      else if (canViewHistory) setTab('history');
      else if (canViewMarketing) setTab('marketing');
    }
  }, [canViewHistory, canViewMain, canViewMarketing, canViewPayments, canViewVisitings, tab]);

  useEffect(() => {
    if (!detail?.id) return;
    setMainForm({
      fio: detail.fio ?? '',
      phone: formatRuMobilePhone(detail.phone ?? ''),
      gender: detail.gender != null ? String(detail.gender) : '1',
      birthday: detail.birthday ? detail.birthday.slice(0, 10) : '',
      comment: detail.comment ?? '',
      status: detail.status ?? '',
      paykeeperSkipCash: Number(detail.paykeeperSkipCash) === 1,
    });
    setDiscountForm({
      saleType: detail.saleType != null ? String(detail.saleType) : '',
      saleAmount: detail.saleAmount != null ? String(detail.saleAmount) : '',
    });
  }, [detail]);

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    crmApi(branchId)
      .get('/settings/client_statuses')
      .then(({ data }) => {
        if (!alive) return;
        const raw = data.items ?? [];
        setStatuses(
          raw.map((it) => ({
            key: it.key,
            name: (it.value && it.value.name) || it.key,
            color:
              it.value && it.value.color && String(it.value.color).trim()
                ? String(it.value.color).trim()
                : '#dfe1e6',
            // Определяет, спрашивать ли дату пробного перед сменой статуса (ТЗ §4–§5).
            trialDateMode: (it.value && it.value.trial_date_mode) || 'none',
          })),
        );
      })
      .catch(() => alive && setStatuses([]));
    return () => {
      alive = false;
    };
  }, [branchId]);

  useEffect(() => {
    if (isNew || tab !== 'main' || !canManageDirections) return;
    let alive = true;
    crmApi(branchId)
      .get('/directions', { params: { limit: 500 } })
      .then(({ data }) => alive && setDirectionOptions(data.items ?? []))
      .catch(() => alive && setDirectionOptions([]));
    return () => {
      alive = false;
    };
  }, [branchId, canManageDirections, isNew, tab]);

  useEffect(() => {
    if (isNew || tab !== 'main' || !canManageDirections || !branchId || !directionQuery.trim()) {
      setDirectionSuggestions([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await crmApi(branchId).get('/directions', { params: { q: directionQuery.trim(), limit: 30 } });
        if (!alive) return;
        const used = new Set(
          (detail?.directions ?? [])
            .filter((d) => d.isActive !== false)
            .map((d) => d.directionId ?? d.id),
        );
        const filtered = (data.items ?? []).filter((d) => !used.has(d.id));
        setDirectionSuggestions(filtered);
      } catch {
        if (alive) setDirectionSuggestions([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [branchId, canManageDirections, detail?.directions, directionQuery, isNew, tab]);

  useEffect(() => {
    if (!directionDraftId || directionDraftLabel) return;
    const found = directionOptions.find((d) => d.id === directionDraftId);
    if (found) setDirectionDraftLabel(found.name ?? found.id);
  }, [directionDraftId, directionDraftLabel, directionOptions]);

  useEffect(() => {
    if (
      isNew ||
      tab !== 'main' ||
      !canManageRelatives ||
      !detail?.id ||
      relDraft ||
      relQuery.trim().length < 2
    ) {
      setRelOptions([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await crmApi(branchId).get('/clients', {
          params: { q: relQuery.trim(), limit: 20 },
        });
        if (!alive) return;
        const used = new Set((detail.relatives ?? []).map((r) => r.id));
        const filtered = (data.items ?? []).filter((c) => c.id !== detail.id && !used.has(c.id));
        setRelOptions(filtered);
      } catch {
        if (alive) setRelOptions([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [branchId, canManageRelatives, detail, isNew, tab, relQuery, relDraft]);

  useEffect(() => {
    if (isNew || tab !== 'payments' || !canViewPayments || !detail?.id) return;
    let alive = true;
    setPayments((s) => ({ ...s, loading: true, error: null }));
    const params = {
      clientId: detail.id,
      limit: 200,
      ...(paymentsFilter.from ? { from: dayStartUtcIso(paymentsFilter.from) } : {}),
      ...(paymentsFilter.to ? { to: dayEndExclusiveUtcIso(paymentsFilter.to) } : {}),
      ...(paymentsStatusFilter !== '' ? { status: Number(paymentsStatusFilter) } : {}),
    };
    crmApi(branchId)
      .get('/payments', { params })
      .then(({ data }) => {
        if (!alive) return;
        setPayments({ items: data.items ?? [], loading: false, error: null });
      })
      .catch((e) => {
        if (!alive) return;
        setPayments({ items: [], loading: false, error: errMsg(e, 'Не удалось загрузить платежи') });
      });
    return () => {
      alive = false;
    };
  }, [branchId, canViewPayments, detail?.id, isNew, tab, paymentsFilter.from, paymentsFilter.to, paymentsStatusFilter]);

  useEffect(() => {
    if (isNew || tab !== 'history' || !canViewHistory || !detail?.id) return;
    let alive = true;
    setHistory((s) => ({ ...s, loading: true, error: null }));
    crmApi(branchId)
      .get(`/clients/${detail.id}/history`, { params: { limit: 300 } })
      .then(({ data }) => {
        if (!alive) return;
        setHistory({ items: data.items ?? [], loading: false, error: null });
      })
      .catch((e) => {
        if (!alive) return;
        setHistory({ items: [], loading: false, error: errMsg(e, 'Не удалось загрузить историю') });
      });
    return () => {
      alive = false;
    };
  }, [branchId, canViewHistory, detail?.id, isNew, tab]);

  useEffect(() => {
    if (isNew || tab !== 'marketing' || !canViewMarketing || !detail?.id) return;
    let alive = true;
    setMarketing((s) => ({ ...s, loading: true, error: null }));
    crmApi(branchId)
      .get(`/clients/${detail.id}/marketing`, { params: { limit: 300 } })
      .then(({ data }) => {
        if (!alive) return;
        setMarketing({ items: data.items ?? [], loading: false, error: null });
      })
      .catch((e) => {
        if (!alive) return;
        setMarketing({ items: [], loading: false, error: errMsg(e, 'Не удалось загрузить маркетинг') });
      });
    return () => {
      alive = false;
    };
  }, [branchId, canViewMarketing, detail?.id, isNew, tab]);

  useEffect(() => {
    if (isNew || tab !== 'visitings' || !canViewVisitings || !detail?.id) return;
    let alive = true;
    setVisitings((s) => ({ ...s, loading: true, error: null }));
    crmApi(branchId)
      .get(`/clients/${detail.id}/visitings`, {
        params: {
          from: visitingsFilter.from,
          to: visitingsFilter.to,
        },
      })
      .then(({ data }) => {
        if (!alive) return;
        setVisitings({ items: data.items ?? [], loading: false, error: null });
      })
      .catch((e) => {
        if (!alive) return;
        setVisitings({ items: [], loading: false, error: errMsg(e, 'Не удалось загрузить посещения') });
      });
    return () => {
      alive = false;
    };
  }, [branchId, canViewVisitings, detail?.id, isNew, tab, visitingsFilter.from, visitingsFilter.to]);

  const {
    control: newControl,
    register: newRegister,
    handleSubmit: handleNewSubmit,
    getValues: getNewValues,
    formState: { errors: newErrors },
  } = useForm({
    defaultValues: { fio: '', phone: '', gender: '1' },
  });

  const duplicateBusy = attaching || creatingDuplicate;

  const closeDuplicateWarning = () => {
    if (duplicateBusy) return;
    setDuplicateWarning(null);
    setAttachError(null);
  };

  const attachToCurrentBranch = async () => {
    if (!duplicateWarning?.clientId || attaching) return;
    setAttachError(null);
    setAttaching(true);
    try {
      await crmApi(branchId).post(
        `/clients/${duplicateWarning.clientId}/attach-current-branch`,
      );
      const id = duplicateWarning.clientId;
      setDuplicateWarning(null);
      navigate(`/${ownerId}/crm/${branchId}/clients/${id}`, { replace: true });
    } catch (ex) {
      setAttachError(errMsg(ex, 'Не удалось привязать клиента к филиалу'));
    } finally {
      setAttaching(false);
    }
  };

  const createDespitePhone = async () => {
    if (!duplicateWarning?.allowCreate || creatingDuplicate) return;
    setAttachError(null);
    setCreatingDuplicate(true);
    try {
      const values = getNewValues();
      const normalizedPhone = normalizeRuMobilePhone(values.phone);
      const { data } = await crmApi(branchId).post('/clients', {
        fio: values.fio.trim(),
        phone: normalizedPhone,
        gender: Number(values.gender),
        allowDuplicatePhone: true,
      });
      setDuplicateWarning(null);
      if (data.client?.id) {
        navigate(`/${ownerId}/crm/${branchId}/clients/${data.client.id}`, { replace: true });
      }
    } catch (ex) {
      setAttachError(errMsg(ex, 'Не удалось создать клиента'));
    } finally {
      setCreatingDuplicate(false);
    }
  };

  const onCreateSubmit = async (values) => {
    if (!canCreateClient) return;
    setNewClientError(null);
    setDuplicateWarning(null);
    setAttachError(null);
    const normalizedPhone = normalizeRuMobilePhone(values.phone);
    if (!normalizedPhone) return;
    setNewClientSaving(true);
    try {
      const { data } = await crmApi(branchId).post('/clients', {
        fio: values.fio.trim(),
        phone: normalizedPhone,
        gender: Number(values.gender),
      });
      if (data.client?.id) {
        navigate(`/${ownerId}/crm/${branchId}/clients/${data.client.id}`, { replace: true });
      }
    } catch (e) {
      const identity = duplicateIdentityConflict(e);
      if (identity) {
        if (identity.canAttach) {
          setDuplicateWarning(identity);
          return;
        }
        setNewClientError(identity.message);
        return;
      }
      const warning = duplicatePhoneWarning(e);
      if (warning) {
        setDuplicateWarning(warning);
        return;
      }
      setNewClientError(errMsg(e, 'Не удалось создать клиента'));
    } finally {
      setNewClientSaving(false);
    }
  };

  const onSaveMainInfo = async () => {
    if (!detail?.id) return;
    setSaveMainError(null);
    const fio = mainForm.fio.trim();
    if (canEditMain && !fio) {
      setSaveMainError('ФИО обязательно');
      return;
    }
    const normalizedPhone =
      canEditMain && mainForm.phone.trim() ? normalizeRuMobilePhone(mainForm.phone) : null;
    if (canEditMain && mainForm.phone.trim() && !normalizedPhone) {
      setSaveMainError('Телефон: формат +7 (9xx) xxx-xx-xx');
      return;
    }
    const saleType = discountForm.saleType === '' ? null : Number(discountForm.saleType);
    const saleAmount = saleType == null || discountForm.saleAmount === ''
      ? null
      : Number(discountForm.saleAmount);
    if (canManageDiscount && saleType != null && (saleAmount == null || Number.isNaN(saleAmount))) {
      setSaveMainError('Укажите сумму постоянной скидки');
      return;
    }
    if (canManageDiscount && saleAmount != null && saleAmount < 0) {
      setSaveMainError('Постоянная скидка не может быть отрицательной');
      return;
    }
    if (canManageDiscount && saleType === 1 && saleAmount != null && saleAmount > 100) {
      setSaveMainError('Процентная скидка не может быть больше 100%');
      return;
    }
    const payload = {};
    if (canEditMain) {
      payload.fio = fio;
      payload.phone = normalizedPhone;
      payload.gender = mainForm.gender === '' ? null : Number(mainForm.gender);
      payload.birthday = mainForm.birthday || null;
      payload.comment = mainForm.comment.trim() || null;
      payload.paykeeperSkipCash = mainForm.paykeeperSkipCash ? 1 : 0;
    }
    if (canManageDiscount) {
      payload.saleType = saleType;
      payload.saleAmount = saleAmount;
    }
    if (Object.keys(payload).length === 0) return;

    setSaveMainLoading(true);
    try {
      await crmApi(branchId).patch(`/clients/${detail.id}`, payload);
      await loadDetail();
    } catch (e) {
      setSaveMainError(errMsg(e, 'Не удалось сохранить данные клиента'));
    } finally {
      setSaveMainLoading(false);
    }
  };

  const onAddDirection = async () => {
    if (!detail?.id || !directionDraftId) return;
    setDirectionError(null);
    setDirectionSaving(true);
    try {
      await crmApi(branchId).post(`/clients/${detail.id}/directions`, {
        directionId: directionDraftId,
      });
      setDirectionDraftId('');
      setDirectionDraftLabel('');
      setDirectionQuery('');
      setDirectionSuggestions([]);
      await loadDetail();
    } catch (e) {
      setDirectionError(errMsg(e, 'Не удалось добавить направление'));
    } finally {
      setDirectionSaving(false);
    }
  };

  const onCloseDirection = async (direction) => {
    if (!detail?.id) return;
    const label = direction.name ?? direction.directionId ?? direction.id;
    if (!window.confirm(`Прекратить занятия по направлению «${label}»?`)) return;
    setDirectionError(null);
    setDirectionSaving(true);
    try {
      if (direction.clientDirectionId) {
        await crmApi(branchId).post(
          `/clients/${detail.id}/directions/${direction.clientDirectionId}/close`,
        );
      } else {
        await crmApi(branchId).delete(
          `/clients/${detail.id}/directions/${direction.directionId ?? direction.id}`,
        );
      }
      await loadDetail();
    } catch (e) {
      setDirectionError(errMsg(e, 'Не удалось прекратить занятия'));
    } finally {
      setDirectionSaving(false);
    }
  };

  /** Одна точка сохранения статуса и даты пробного: backend меняет их транзакцией (ТЗ §8). */
  const saveDirectionStatus = async (direction, nextStatus, trialLessonDate) => {
    const body = { status: nextStatus };
    if (trialLessonDate !== undefined) body.trialLessonDate = trialLessonDate;
    await crmApi(branchId).patch(
      `/clients/${detail.id}/directions/${direction.clientDirectionId}/status`,
      body,
    );
    await loadDetail();
  };

  const onDirectionStatusChange = async (direction, nextStatus) => {
    if (!detail?.id || !direction.clientDirectionId || !nextStatus) return;
    setDirectionError(null);

    // Статус требует дату — сначала спрашиваем её, статус не меняем (ТЗ §4–§5).
    const mode = statuses.find((s) => s.key === nextStatus)?.trialDateMode ?? 'none';
    if (mode !== 'none') {
      setTrialRequest({ direction, status: nextStatus, mode });
      return;
    }

    setDirectionSaving(true);
    try {
      await saveDirectionStatus(direction, nextStatus);
    } catch (e) {
      setDirectionError(errMsg(e, 'Не удалось изменить статус направления'));
    } finally {
      setDirectionSaving(false);
    }
  };

  /** История переносов пробного по конкретному участию (ТЗ §7, §10). */
  const onToggleTrialHistory = async (direction) => {
    const id = direction.clientDirectionId;
    if (trialHistory.clientDirectionId === id) {
      setTrialHistory({ clientDirectionId: null, items: [], loading: false, error: null });
      return;
    }
    setTrialHistory({ clientDirectionId: id, items: [], loading: true, error: null });
    try {
      const { data } = await crmApi(branchId).get(
        `/clients/${detail.id}/directions/${id}/trial-date-history`,
      );
      setTrialHistory({
        clientDirectionId: id,
        items: data.items ?? [],
        loading: false,
        error: null,
      });
    } catch (e) {
      setTrialHistory({
        clientDirectionId: id,
        items: [],
        loading: false,
        error: errMsg(e, 'Не удалось загрузить историю дат'),
      });
    }
  };

  /** Изменение даты без смены статуса — повторный перенос по ТЗ §6. */
  const onTrialDateEdit = (direction) => {
    setDirectionError(null);
    setTrialRequest({
      direction,
      status: direction.status,
      mode: direction.trialDateMode === 'none' ? 'set_required' : direction.trialDateMode,
    });
  };

  const onTrialDateConfirm = async (date) => {
    if (!trialRequest) return;
    setDirectionSaving(true);
    setTrialError(null);
    try {
      await saveDirectionStatus(trialRequest.direction, trialRequest.status, date);
      setTrialRequest(null);
    } catch (e) {
      setTrialError(
        TRIAL_DATE_ERROR_LABELS[errCode(e)] ?? errMsg(e, 'Не удалось сохранить дату пробного'),
      );
    } finally {
      setDirectionSaving(false);
    }
  };

  const clearRelDraft = () => {
    setRelDraft(null);
    setRelQuery('');
    setRelOptions([]);
  };

  const onAddRelative = async () => {
    const relativeId = relDraft?.id;
    if (!detail?.id || !relativeId || !relRelationship) return;
    setRelError(null);
    setRelSaving(true);
    try {
      await crmApi(branchId).post(`/clients/${detail.id}/relatives`, {
        relativeId,
        relationship: relRelationship,
      });
      clearRelDraft();
      await loadDetail();
    } catch (e) {
      setRelError(errMsg(e, 'Не удалось добавить родственника'));
    } finally {
      setRelSaving(false);
    }
  };

  const onRemoveRelative = async (relativeId) => {
    if (!detail?.id) return;
    setRelError(null);
    try {
      await crmApi(branchId).delete(`/clients/${detail.id}/relatives/${relativeId}`);
      await loadDetail();
    } catch (e) {
      setRelError(errMsg(e, 'Не удалось удалить родственника'));
    }
  };

  const onCreateAndAddRelative = async (createdClient) => {
    if (!createdClient?.id) return;
    setRelDraft({
      id: createdClient.id,
      fio: createdClient.fio ?? '(без имени)',
    });
    setRelQuery(createdClient.fio ?? '');
    setRelOptions([]);
    setRelError(null);
  };

  const onAddBranch = async () => {
    if (!detail?.id || !branchDraftId) return;
    setBranchError(null);
    setBranchSaving(true);
    try {
      await crmApi(branchId).post(`/clients/${detail.id}/branches`, { branchId: branchDraftId });
      setBranchDraftId('');
      await loadDetail();
    } catch (e) {
      setBranchError(errMsg(e, 'Не удалось добавить филиал'));
    } finally {
      setBranchSaving(false);
    }
  };

  const onRemoveBranch = async (targetBranchId) => {
    if (!detail?.id || !targetBranchId) return;
    if ((detail?.branches ?? []).length <= 1) {
      setBranchError('Нельзя удалить последний филиал клиента');
      return;
    }
    setBranchError(null);
    setBranchSaving(true);
    try {
      await crmApi(branchId).delete(`/clients/${detail.id}/branches/${targetBranchId}`);
      if (targetBranchId === branchId) {
        navigate(backTo, { replace: true });
        return;
      }
      await loadDetail();
    } catch (e) {
      setBranchError(errMsg(e, 'Не удалось удалить филиал'));
    } finally {
      setBranchSaving(false);
    }
  };

  const historyFiltered = useMemo(() => {
    const q = historyFilter.q.trim().toLowerCase();
    return history.items.filter((h) => {
      const day = h.dateTime ? h.dateTime.slice(0, 10) : '';
      if (historyFilter.from && day && day < historyFilter.from) return false;
      if (historyFilter.to && day && day > historyFilter.to) return false;
      if (!q) return true;
      const hay = `${h.key ?? ''} ${h.value ?? ''} ${h.managerName ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [history.items, historyFilter.from, historyFilter.to, historyFilter.q]);

  const marketingRows = useMemo(() => flattenMarketingItems(marketing.items), [marketing.items]);

  const marketingFiltered = useMemo(() => {
    const q = marketingFilter.q.trim().toLowerCase();
    return marketingRows.filter((row) => {
      const day = row.dateTime ? row.dateTime.slice(0, 10) : '';
      if (marketingFilter.from && day && day < marketingFilter.from) return false;
      if (marketingFilter.to && day && day > marketingFilter.to) return false;
      if (!q) return true;
      const hay = `${row.key ?? ''} ${row.value ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [marketingRows, marketingFilter.from, marketingFilter.to, marketingFilter.q]);

  const selectDirection = (d) => {
    setDirectionDraftId(d.id);
    setDirectionDraftLabel(d.name ?? d.id);
    setDirectionQuery('');
    setDirectionSuggestions([]);
  };

  const clearDirectionDraft = () => {
    setDirectionDraftId('');
    setDirectionDraftLabel('');
    setDirectionQuery('');
    setDirectionSuggestions([]);
  };

  const branchSelectOptions = useMemo(() => {
    const used = new Set((detail?.branches ?? []).map((b) => b.id));
    return crmBranches
      .filter((b) => !used.has(b.id))
      .map((b) => ({ value: b.id, label: b.name ?? b.id }));
  }, [crmBranches, detail?.branches]);

  const openSaleDetailModal = (saleId) => {
    setPaymentActionError(null);
    setSaleModal({ open: true, saleId, data: null, loading: true, error: null });
    crmApi(branchId)
      .get(`/sales/${saleId}`)
      .then(({ data }) => {
        setSaleModal((m) => ({ ...m, loading: false, data: data.sale ?? null }));
      })
      .catch((e) => {
        setSaleModal((m) => ({
          ...m,
          loading: false,
          error: errMsg(e, 'Не удалось загрузить позиции заказа'),
        }));
      });
  };

  const closeSaleModal = () => {
    setSaleModal({ open: false, saleId: null, data: null, loading: false, error: null });
  };

  const onPaymentChangeStatus = async (saleId, status) => {
    setPaymentActionError(null);
    setPaymentStatusBusyId(saleId);
    try {
      await crmApi(branchId).patch(`/sales/${saleId}`, { status });
      setPayments((s) => ({
        ...s,
        items: s.items.map((row) => (row.id === saleId ? { ...row, status } : row)),
      }));
    } catch (e) {
      setPaymentActionError(errMsg(e, 'Не удалось обновить статус'));
    } finally {
      setPaymentStatusBusyId(null);
    }
  };

  const onPaymentOpenCheque = async (saleId) => {
    setPaymentActionError(null);
    setPaymentChequeBusyId(saleId);
    try {
      const { data } = await crmApi(branchId).get(`/sales/${saleId}/cheque`);
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setPaymentActionError(errMsg(e, 'Не удалось открыть чек'));
    } finally {
      setPaymentChequeBusyId(null);
    }
  };

  const onPaymentReissueCode = async (saleId) => {
    if (paymentCodeBusyId) return;
    setPaymentActionError(null);
    setPaymentCodeBusyId(saleId);
    try {
      const { data } = await crmApi(branchId).post(`/sales/${saleId}/payment-code`);
      setPaymentCodeModal(data?.code ?? null);
    } catch (e) {
      setPaymentActionError(errMsg(e, 'Не удалось выпустить код оплаты'));
    } finally {
      setPaymentCodeBusyId(null);
    }
  };

  return (
    <section className="clients-page clients-page--detail">
      <header className="page-header page-header--spread">
        <div>
          <Link to={backTo} className="clients-page__back">
            ← Клиенты
          </Link>
        </div>
      </header>

      <div className="form-card form-card--wide">
        {loadingDetail ? (
          <div className="placeholder">Загрузка…</div>
        ) : isNew ? (
          <form onSubmit={handleNewSubmit(onCreateSubmit)}>
            <div className="form-grid">
              <UiInput
                label="ФИО"
                className="field"
                style={{ gridColumn: '1 / -1' }}
                error={newErrors.fio?.message}
                {...newRegister('fio', { required: 'Обязательно' })}
              />
              <label className="ui-field field">
                <span className="ui-field__label">Телефон</span>
                <Controller
                  name="phone"
                  control={newControl}
                  rules={{
                    validate: (v) =>
                      normalizeRuMobilePhone(v) ? true : 'Формат: +7 (9xx) xxx-xx-xx',
                  }}
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      className="ui-control"
                      mask={RU_MOBILE_MASK}
                      formatChars={RU_MOBILE_FORMAT_CHARS}
                      maskChar={null}
                      placeholder="+7 (9xx) xxx-xx-xx"
                    />
                  )}
                />
                {newErrors.phone && <span className="form-error">{newErrors.phone.message}</span>}
              </label>
              <Controller
                name="gender"
                control={newControl}
                render={({ field }) => (
                  <UiSelect
                    className="field"
                    label="Пол"
                    value={field.value}
                    onChange={field.onChange}
                    options={GENDER_OPTIONS}
                  />
                )}
              />
            </div>
            {(newClientError || saveError) && (
              <div className="form-error">{newClientError || saveError}</div>
            )}
            <div className="form-actions left">
              <UiButton
                type="submit"
                disabled={newClientSaving || duplicateBusy || saveStatus === 'loading'}
              >
                {newClientSaving || saveStatus === 'loading' ? 'Создание…' : 'Создать'}
              </UiButton>
              <UiLinkButton to={backTo} variant="ghost">
                Отменить
              </UiLinkButton>
            </div>
            {duplicateWarning ? (
              <div
                className="modal-backdrop"
                role="presentation"
                onClick={closeDuplicateWarning}
              >
                <div
                  className="modal-card"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="duplicate-client-title"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  <header className="modal-card__header">
                    <h2 id="duplicate-client-title">Клиент уже существует</h2>
                    <UiButton
                      variant="ghost"
                      size="sm"
                      type="button"
                      disabled={duplicateBusy}
                      onClick={closeDuplicateWarning}
                      aria-label="Закрыть"
                    >
                      ×
                    </UiButton>
                  </header>
                  <div className="modal-card__body">
                    <p style={{ marginTop: 0 }}>{duplicateWarning.message}</p>
                    {attachError ? <div className="form-error">{attachError}</div> : null}
                    <div className="modal-card__actions">
                      {duplicateWarning.canAttach ? (
                        <UiButton
                          type="button"
                          disabled={duplicateBusy}
                          onClick={attachToCurrentBranch}
                        >
                          {attaching ? 'Привязка…' : 'Привязать к текущему филиалу'}
                        </UiButton>
                      ) : null}
                      {duplicateWarning.allowCreate ? (
                        <UiButton
                          type="button"
                          disabled={duplicateBusy}
                          onClick={createDespitePhone}
                        >
                          {creatingDuplicate ? 'Создание…' : 'Создать нового'}
                        </UiButton>
                      ) : null}
                      <UiButton
                        variant="ghost"
                        type="button"
                        disabled={duplicateBusy}
                        onClick={closeDuplicateWarning}
                      >
                        Отменить
                      </UiButton>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </form>
        ) : (
          <>
            <nav className="clients-page__tabs" aria-label="Разделы клиента">
              {canViewMain && (
                <button type="button" className={tab === 'main' ? 'is-active' : ''} onClick={() => setTab('main')}>
                  Основная информация
                </button>
              )}
              {canViewPayments && (
                <button
                  type="button"
                  className={tab === 'payments' ? 'is-active' : ''}
                  onClick={() => setTab('payments')}
                >
                  Платежи
                </button>
              )}
              {canViewVisitings && (
                <button
                  type="button"
                  className={tab === 'visitings' ? 'is-active' : ''}
                  onClick={() => setTab('visitings')}
                >
                  Посещения
                </button>
              )}
              {canViewHistory && (
                <button type="button" className={tab === 'history' ? 'is-active' : ''} onClick={() => setTab('history')}>
                  История
                </button>
              )}
              {canViewMarketing && (
                <button
                  type="button"
                  className={tab === 'marketing' ? 'is-active' : ''}
                  onClick={() => setTab('marketing')}
                >
                  Маркетинг
                </button>
              )}
            </nav>

            {!canViewMain && !canViewPayments && !canViewVisitings && !canViewHistory && !canViewMarketing ? (
              <div className="form-card form-card--wide">
                <div className="muted">Недостаточно прав для просмотра карточки клиента.</div>
              </div>
            ) : null}

            {canViewMain && tab === 'main' ? (
              <div className="clients-page__detail-grid clients-page__detail-grid--profile">
                <section className="clients-page__detail-block clients-page__detail-block--main">
                  <div className="form-grid">
                    <UiInput
                      label="Ф.И.О."
                      className="field"
                      style={{ gridColumn: '1 / -1' }}
                      value={mainForm.fio}
                      disabled={!canEditMain}
                      onChange={(e) => setMainForm((p) => ({ ...p, fio: e.target.value }))}
                    />
                    <label className="ui-field field">
                      <span className="ui-field__label">Телефон</span>
                      <InputMask
                        value={mainForm.phone}
                        onChange={(e) => setMainForm((p) => ({ ...p, phone: e.target.value }))}
                        className="ui-control"
                        disabled={!canEditMain}
                        mask={RU_MOBILE_MASK}
                        formatChars={RU_MOBILE_FORMAT_CHARS}
                        maskChar={null}
                        placeholder="+7 (9xx) xxx-xx-xx"
                      />
                    </label>
                    <div className="field">
                      <label>Общий статус</label>
                      <div className="ui-control" style={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                        {(() => {
                          const key = detail?.status ?? mainForm.status;
                          if (!key) return '—';
                          if (detail?.statusName) return detail.statusName;
                          const found = statuses.find((s) => String(s.key) === String(key));
                          return found?.name ?? key;
                        })()}
                      </div>
                      <span className="muted" style={{ fontSize: 12 }}>
                        Сводка по направлениям филиала. Меняйте статус у каждого направления справа.
                      </span>
                    </div>
                    <UiSelect
                      className="field"
                      label="Пол"
                      value={mainForm.gender}
                      disabled={!canEditMain}
                      onChange={(v) => setMainForm((p) => ({ ...p, gender: v }))}
                      options={GENDER_OPTIONS}
                    />
                    <UiInput
                      className="field"
                      label="Дата рождения"
                      type="date"
                      value={mainForm.birthday}
                      disabled={!canEditMain}
                      onChange={(e) => setMainForm((p) => ({ ...p, birthday: e.target.value }))}
                    />
                  </div>
                </section>

                <section className="clients-page__detail-block clients-page__detail-block--comment">
                  <UiTextarea
                    className="field"
                    label="Комментарий"
                    rows={6}
                    value={mainForm.comment}
                    disabled={!canEditMain}
                    onChange={(e) => setMainForm((p) => ({ ...p, comment: e.target.value }))}
                  />
                  <UiCheckbox
                    className="clients-page__check"
                    style={{ marginTop: 12 }}
                    label="Отключить PayKeeper"
                    title="Для оплаты «Наличные» не отправлять чек в PayKeeper — только сохранить продажу в базе"
                    checked={mainForm.paykeeperSkipCash}
                    disabled={!canEditMain}
                    onChange={(e) => setMainForm((p) => ({
                      ...p,
                      paykeeperSkipCash: e.target.checked,
                    }))}
                  />
                </section>

                <section className="clients-page__detail-block clients-page__detail-block--contacts">
                  <ClientContacts
                    branchId={branchId}
                    clientId={detail.id}
                    contacts={detail.contacts ?? []}
                    canManage={canManageContacts}
                    onChanged={loadDetail}
                  />
                </section>

                <section className="clients-page__detail-block clients-page__detail-block--discount">
                  <h3>Постоянная скидка</h3>
                  <UiSelect
                    className="field"
                    label="Тип скидки"
                    value={discountForm.saleType}
                    disabled={!canManageDiscount}
                    onChange={(v) => setDiscountForm((p) => ({
                      ...p,
                      saleType: v,
                      saleAmount: v === '' ? '' : p.saleAmount,
                    }))}
                    options={DISCOUNT_TYPE_OPTIONS}
                  />
                  <UiInput
                    className="field"
                    label="Сумма скидки"
                    type="number"
                    value={discountForm.saleAmount}
                    disabled={!canManageDiscount}
                    onChange={(e) => setDiscountForm((p) => ({ ...p, saleAmount: e.target.value }))}
                  />
                </section>

                <section className="clients-page__detail-block clients-page__detail-block--directions">
                  <h3>Направления</h3>
                  {canManageDirections && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <UiAutosuggest
                          className="field clients-page__autosuggest"
                          label="Добавить направление"
                          value={directionDraftId ? directionDraftLabel : directionQuery}
                          onChange={(value) => {
                            if (directionDraftId) clearDirectionDraft();
                            setDirectionQuery(value);
                          }}
                          suggestions={directionSuggestions}
                          onSelect={selectDirection}
                          placeholder="Начните вводить название..."
                          onClear={clearDirectionDraft}
                          emptyText=""
                          getOptionKey={(item) => item.id}
                          getOptionLabel={(item) => item.name}
                        />
                      </div>
                      <UiButton
                        type="button"
                        size="sm"
                        disabled={!directionDraftId || directionSaving}
                        onClick={onAddDirection}
                        aria-label="Добавить направление"
                      >
                        <svg width="16" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7 0C6.58579 0 6.25 0.335786 6.25 0.75V6.25H0.75C0.335786 6.25 0 6.58579 0 7C0 7.41421 0.335786 7.75 0.75 7.75H6.25V13.25C6.25 13.6642 6.58579 14 7 14C7.41421 14 7.75 13.6642 7.75 13.25V7.75H13.25C13.6642 7.75 14 7.41421 14 7C14 6.58579 13.6642 6.25 13.25 6.25H7.75V0.75C7.75 0.335786 7.41421 0 7 0Z"
                            fill="currentColor"
                          />
                        </svg>
                      </UiButton>
                    </div>
                  )}
                  {(detail?.roles ?? []).length ? (
                    <div className="muted" style={{ marginBottom: 8 }}>
                      Роли: {(detail.roles ?? []).map((r) => (r === 'parent' ? 'Родитель' : r === 'student' ? 'Ученик' : r)).join(', ')}
                    </div>
                  ) : null}
                  <ul className="plain-list">
                    {(detail?.directions ?? []).map((d) => (
                      <li
                        key={d.clientDirectionId ?? d.id}
                        className="plain-list__item"
                        style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}
                      >
                        <div style={{ flex: 1, minWidth: 160 }}>
                          <div>
                            {d.name ?? d.directionId ?? d.id}
                            {d.isActive === false ? ' (закрыто)' : ''}
                          </div>
                          {d.startedAt ? (
                            <div className="muted" style={{ fontSize: 12 }}>
                              с {String(d.startedAt).slice(0, 10)}
                              {d.endedAt ? ` по ${String(d.endedAt).slice(0, 10)}` : ''}
                            </div>
                          ) : null}
                          {d.isActive !== false && (d.trialLessonDate || d.trialDateMode !== 'none') ? (
                            <div className="clients-page__trial-row">
                              {(() => {
                                const info = describeTrialDate(d.trialLessonDate, today);
                                if (!info) return <span className="muted">Пробное: не назначено</span>;
                                return (
                                  <span
                                    className={`clients-page__trial clients-page__trial--${info.tone}`}
                                  >
                                    {info.text}
                                  </span>
                                );
                              })()}
                              {canUpdateStatus ? (
                                <UiButton
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={directionSaving}
                                  onClick={() => onTrialDateEdit(d)}
                                >
                                  {d.trialLessonDate ? 'Перенести' : 'Назначить'}
                                </UiButton>
                              ) : null}
                              <UiButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onToggleTrialHistory(d)}
                              >
                                {trialHistory.clientDirectionId === d.clientDirectionId
                                  ? 'Скрыть историю'
                                  : 'История'}
                              </UiButton>
                            </div>
                          ) : null}
                          {trialHistory.clientDirectionId === d.clientDirectionId ? (
                            <div className="clients-page__trial-history">
                              {trialHistory.loading ? <span className="muted">Загрузка…</span> : null}
                              {trialHistory.error ? (
                                <span className="form-error">{trialHistory.error}</span>
                              ) : null}
                              {!trialHistory.loading && !trialHistory.error
                                ? trialHistory.items.map((it) => (
                                    <div key={it.id}>
                                      {formatTrialDateShort(it.oldDate)} →{' '}
                                      {formatTrialDateShort(it.newDate)} ·{' '}
                                      {TRIAL_DATE_CHANGE_TYPE_LABELS[it.changeType] ?? it.changeType}
                                      {it.changedByName ? ` · ${it.changedByName}` : ''}
                                    </div>
                                  ))
                                : null}
                              {!trialHistory.loading && !trialHistory.error && !trialHistory.items.length ? (
                                <span className="muted">Изменений не было</span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        {d.isActive !== false && canUpdateStatus ? (
                          <div style={{ minWidth: 160 }}>
                            <ClientStatusSelect
                              value={d.status ?? ''}
                              onChange={(key) => onDirectionStatusChange(d, key)}
                              statuses={statuses}
                              disabled={directionSaving}
                              allowEmpty={false}
                              emptyLabel="—"
                            />
                          </div>
                        ) : (
                          <span className="muted">{d.statusName || d.status || '—'}</span>
                        )}
                        {canManageDirections && d.isActive !== false && (
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={directionSaving}
                            onClick={() => onCloseDirection(d)}
                            title="Прекратить занятия"
                          >
                            Прекратить
                          </UiButton>
                        )}
                      </li>
                    ))}
                    {!(detail?.directions ?? []).length ? (
                      <li className="clients-page__detail-empty">
                        {(detail?.roles ?? []).includes('parent') && !(detail?.roles ?? []).includes('student')
                          ? 'Собственных направлений нет'
                          : 'Нет направлений'}
                      </li>
                    ) : null}
                  </ul>
                  {directionError ? <div className="form-error">{directionError}</div> : null}
                </section>

                <section className="clients-page__detail-block clients-page__detail-block--relatives">
                  <h3>Родственники</h3>
                  {canManageRelatives && (
                    <>
                      {relDraft ? (
                        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
                          <div className="clients-page__detail-hint">
                            Выбран: <strong>{relDraft.fio ?? '(без имени)'}</strong>
                          </div>
                          <UiSelect
                            className="field"
                            label="Кем приходится текущему клиенту"
                            value={relRelationship}
                            onChange={setRelRelationship}
                            options={RELATIVE_RELATIONSHIP_OPTIONS}
                            disabled={relSaving}
                          />
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <UiButton
                              type="button"
                              size="sm"
                              onClick={onAddRelative}
                              disabled={relSaving || !relRelationship}
                            >
                              Добавить
                            </UiButton>
                            <UiButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={clearRelDraft}
                              disabled={relSaving}
                            >
                              Отмена
                            </UiButton>
                          </div>
                        </div>
                      ) : (
                        <>
                          <UiAutosuggest
                            className="field"
                            label="Добавить родственника"
                            value={relQuery}
                            onChange={setRelQuery}
                            suggestions={relOptions}
                            onSelect={(item) => {
                              setRelDraft({
                                id: item.id,
                                fio: item.fio ?? '(без имени)',
                              });
                              setRelQuery(item.fio ?? '');
                              setRelOptions([]);
                              setRelError(null);
                            }}
                            getOptionLabel={(item) => item.suggestLabel || item.fio || '(без имени)'}
                            getOptionKey={(item) => item.id}
                            placeholder="Начните вводить ФИО..."
                            disabled={relSaving}
                          />
                          <div style={{ marginBottom: 10 }}>
                            <UiButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setRelCreateOpen(true)}
                              disabled={relSaving}
                            >
                              Создать нового родственника
                            </UiButton>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <ul className="plain-list">
                    {(detail?.relatives ?? []).map((r) => (
                      <li key={r.id} className="plain-list__item">
                        <span>
                          {r.fio ?? '(без имени)'}
                          {r.relationship ? ` — ${r.relationship}` : ''}
                        </span>
                        {canManageRelatives && (
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveRelative(r.id)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                          </UiButton>
                        )}
                      </li>
                    ))}
                    {!(detail?.relatives ?? []).length ? <li className="clients-page__detail-empty">Нет родственников</li> : null}
                  </ul>
                  {relError ? <div className="form-error">{relError}</div> : null}
                </section>

                <section className="clients-page__detail-block clients-page__detail-block--branches">
                  <h3>Филиалы</h3>
                  {canManageBranches && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
                      <UiSelect
                        className="field"
                        style={{ flex: 1 }}
                        label="Добавить филиал"
                        value={branchDraftId}
                        onChange={setBranchDraftId}
                        options={branchSelectOptions}
                        placeholder="Выберите филиал"
                      />
                      <UiButton
                        type="button"
                        size="sm"
                        disabled={!branchDraftId || branchSaving}
                        onClick={onAddBranch}
                        aria-label="Добавить филиал"
                      >
                        <svg width="16" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7 0C6.58579 0 6.25 0.335786 6.25 0.75V6.25H0.75C0.335786 6.25 0 6.58579 0 7C0 7.41421 0.335786 7.75 0.75 7.75H6.25V13.25C6.25 13.6642 6.58579 14 7 14C7.41421 14 7.75 13.6642 7.75 13.25V7.75H13.25C13.6642 7.75 14 7.41421 14 7C14 6.58579 13.6642 6.25 13.25 6.25H7.75V0.75C7.75 0.335786 7.41421 0 7 0Z"
                            fill="currentColor"
                          />
                        </svg>
                      </UiButton>
                    </div>
                  )}
                  {(detail?.branches ?? []).length ? (
                    <ul className="plain-list">
                      {detail.branches.map((b) => (
                        <li key={b.id} className="plain-list__item">
                          <span>{b.name ?? b.id}</span>
                          {canManageBranches && (
                            <UiButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={(detail?.branches ?? []).length <= 1 || branchSaving}
                              onClick={() => onRemoveBranch(b.id)}
                              aria-label={`Удалить филиал ${b.name ?? b.id}`}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                            </UiButton>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="clients-page__detail-empty">Нет филиалов</div>
                  )}
                  {canManageBranches && (detail?.branches ?? []).length <= 1 ? (
                    <div className="clients-page__detail-empty">Удаление доступно только если у клиента больше одного филиала</div>
                  ) : null}
                  {branchError ? <div className="form-error">{branchError}</div> : null}
                </section>

                {(canEditMain || canUpdateStatus || canManageDiscount) && (
                  <section className="clients-page__detail-save">
                    {saveMainError ? <div className="form-error">{saveMainError}</div> : null}
                    <UiButton type="button" disabled={saveMainLoading} onClick={onSaveMainInfo}>
                      {saveMainLoading ? 'Сохранение…' : 'Сохранить'}
                    </UiButton>
                  </section>
                )}
              </div>
            ) : null}
            {canManageRelatives && (
              <ClientsNewClientModal
                open={relCreateOpen}
                onClose={() => setRelCreateOpen(false)}
                navigateOnCreate={false}
                asRelative
                onCreated={onCreateAndAddRelative}
              />
            )}

            {trialRequest && (
              <TrialDateModal
                mode={trialRequest.mode}
                currentDate={trialRequest.direction.trialLessonDate}
                subtitle={trialRequest.direction.name ?? undefined}
                today={today}
                busy={directionSaving}
                error={trialError}
                onSubmit={onTrialDateConfirm}
                onClose={() => {
                  setTrialRequest(null);
                  setTrialError(null);
                }}
              />
            )}

            {tab === 'payments' ? (
              <section>
                <div className="form-grid" style={{ marginBottom: 12 }}>
                  <UiInput
                    className="field"
                    label="С даты"
                    type="date"
                    value={paymentsFilter.from}
                    onChange={(e) => setPaymentsFilter((p) => ({ ...p, from: e.target.value }))}
                  />
                  <UiInput
                    className="field"
                    label="По дату"
                    type="date"
                    value={paymentsFilter.to}
                    onChange={(e) => setPaymentsFilter((p) => ({ ...p, to: e.target.value }))}
                  />
                  <UiSelect
                    className="field"
                    label="Статус"
                    value={paymentsStatusFilter}
                    onChange={(v) => setPaymentsStatusFilter(v ?? '')}
                    options={PAYMENT_STATUS_FILTER_OPTIONS}
                    placeholder="Все"
                  />
                </div>
                {payments.loading ? <div className="placeholder">Загрузка платежей…</div> : null}
                {payments.error ? <div className="form-error">{payments.error}</div> : null}
                {paymentActionError ? <div className="form-error clients-page__list-error">{paymentActionError}</div> : null}
                {!payments.loading && !payments.error ? (
                  <>
                    <div className="clients-page__payment-legend" aria-label="Обозначение цветов платежей">
                      <span className="clients-page__payment-legend-title">Цвет строк:</span>
                      {PAYMENT_ROW_LEGEND.map((item) => (
                        <span key={item.key} className="clients-page__payment-legend-item">
                          <span
                            className={`clients-page__payment-legend-color clients-page__payment-legend-color--${item.key}`}
                            aria-hidden
                          />
                          {item.label}
                        </span>
                      ))}
                    </div>
                    <div className="clients-page__table-wrap">
                      <table className="data-table clients-page__payments-table">
                      <thead>
                        <tr>
                          <th className="clients-page__col-quick" aria-hidden />
                          <th>#</th>
                          <th>Дата</th>
                          <th>Статус</th>
                          <th className="clients-page__col-num">Сумма</th>
                          <th>Тип оплаты</th>
                          <th>Менеджер</th>
                          <th>Комментарий</th>
                          <th className="clients-page__payment-actions-col"> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.items.map((p) => {
                          const st = Number(p.status);
                          const pt = p.paymentType != null ? Number(p.paymentType) : 0;
                          const rowClass = `clients-page__payment-row clients-page__payment-row--type-${pt} clients-page__payment-row--status-${st}`;
                          const busy = paymentStatusBusyId === p.id;
                          return (
                            <tr key={p.id} className={rowClass}>
                              <td className="clients-page__col-quick">
                                {pt === 3 ? (
                                  <span className="clients-page__payment-gift" title="Подарок" aria-hidden>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 6a4 4 0 0 0-4-4 4 4 0 0 0-4 4v2H2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8h-2V6a4 4 0 0 0-4-4 4 4 0 0 0-4 4 4 4 0 0 0-4-4zm-2 4V6a2 2 0 1 1 4 0v4H8V10zm8 0V6a2 2 0 1 0-4 0v4h4z" />
                                    </svg>
                                  </span>
                                ) : null}
                              </td>
                              <td className="clients-page__td-nowrap">{p.legacyId || p.id.slice(0, 8)}</td>
                              <td>{fmtPaymentDateTime(p.paidAt)}</td>
                              <td>{SALE_STATUS_LABELS[st] ?? `— (${st})`}</td>
                              <td className="clients-page__col-num">
                                {Number(p.amount ?? 0).toLocaleString('ru-RU')} ₽
                              </td>
                              <td>{SALE_PAYMENT_TYPE_LABELS[pt] ?? (pt ? `Тип ${pt}` : '—')}</td>
                              <td>{p.managerName || '—'}</td>
                              <td>{p.comment || '—'}</td>
                              <td className="clients-page__payment-actions-col">
                                <div className="clients-page__payment-actions">
                                  {canViewPaymentItems && (
                                    <button
                                      type="button"
                                      className="clients-page__icon-btn"
                                      title="Позиции заказа"
                                      aria-label="Позиции заказа"
                                      onClick={() => openSaleDetailModal(p.id)}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path
                                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                      </svg>
                                    </button>
                                  )}
                                  {canManagePaymentStatus && st !== 2 ? (
                                    <button
                                      type="button"
                                      className="clients-page__icon-btn"
                                      title="Подтвердить (оплачен)"
                                      aria-label="Подтвердить оплату"
                                      disabled={busy}
                                      onClick={() => onPaymentChangeStatus(p.id, 2)}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path
                                          d="M20 6L9 17l-5-5"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </button>
                                  ) : null}
                                  {canManagePaymentStatus && st !== 3 ? (
                                    <button
                                      type="button"
                                      className="clients-page__icon-btn clients-page__icon-btn--danger"
                                      title="Возврат"
                                      aria-label="Возврат"
                                      disabled={busy}
                                      onClick={() => onPaymentChangeStatus(p.id, 3)}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path
                                          d="M3 12a9 9 0 1 0 3-7.1"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                        <path d="M3 4v4h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                      </svg>
                                    </button>
                                  ) : null}
                                  {canManagePaymentStatus && st !== 0 ? (
                                    <button
                                      type="button"
                                      className="clients-page__icon-btn"
                                      title="Отменить"
                                      aria-label="Отменить"
                                      disabled={busy}
                                      onClick={() => onPaymentChangeStatus(p.id, 0)}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                                        <path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" strokeWidth="2" />
                                      </svg>
                                    </button>
                                  ) : null}
                                  {canViewPaymentCheque && (
                                    <button
                                      type="button"
                                      className="clients-page__icon-btn"
                                      title="Чек"
                                      aria-label="Открыть чек"
                                      disabled={paymentChequeBusyId === p.id}
                                      onClick={() => onPaymentOpenCheque(p.id)}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <path
                                          d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h2M18 18h2a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2h-2"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        />
                                        <path d="M6 14h12" stroke="currentColor" strokeWidth="2" />
                                      </svg>
                                    </button>
                                  )}
                                  {pt === 1 && (
                                    <button
                                      type="button"
                                      className="clients-page__icon-btn"
                                      title="Новый код оплаты"
                                      aria-label="Выпустить новый код оплаты"
                                      disabled={paymentCodeBusyId === p.id}
                                      onClick={() => onPaymentReissueCode(p.id)}
                                    >
                                      <span
                                        className="clients-page__payment-code-icon"
                                        style={{ maskImage: `url(${pinCodeIcon})`, WebkitMaskImage: `url(${pinCodeIcon})` }}
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {!payments.items.length ? (
                          <tr>
                            <td colSpan={9} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                            Платежей пока нет
                          </td>
                        </tr>
                      ) : null}
                      </tbody>
                      </table>
                    </div>
                  </>
                ) : null}
              </section>
            ) : null}

            {tab === 'visitings' ? (
              <section>
                <div className="form-grid" style={{ marginBottom: 12 }}>
                  <UiDateRangePicker
                    className="field"
                    label="Период"
                    from={visitingsFilter.from}
                    to={visitingsFilter.to}
                    onChange={setVisitingsFilter}
                  />
                </div>
                {visitings.loading ? <div className="placeholder">Загрузка посещений…</div> : null}
                {visitings.error ? <div className="form-error">{visitings.error}</div> : null}
                {!visitings.loading && !visitings.error ? (
                  <div className="clients-page__table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Дата</th>
                          <th>Направление</th>
                          <th>Время отметки</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitings.items.map((v) => (
                          <tr key={v.id}>
                            <td>{v.day || '—'}</td>
                            <td>{v.directionName || 'Без направления'}</td>
                            <td>{fmtDateTime(v.visitIn)}</td>
                          </tr>
                        ))}
                        {!visitings.items.length ? (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                              Посещений за выбранный период нет
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </section>
            ) : null}

            {tab === 'history' ? (
              <section>
                <div className="form-grid" style={{ marginBottom: 12 }}>
                  <UiInput
                    className="field"
                    label="С даты"
                    type="date"
                    value={historyFilter.from}
                    onChange={(e) => setHistoryFilter((p) => ({ ...p, from: e.target.value }))}
                  />
                  <UiInput
                    className="field"
                    label="По дату"
                    type="date"
                    value={historyFilter.to}
                    onChange={(e) => setHistoryFilter((p) => ({ ...p, to: e.target.value }))}
                  />
                  <UiInput
                    className="field"
                    label="Событие / значение / менеджер"
                    value={historyFilter.q}
                    onChange={(e) => setHistoryFilter((p) => ({ ...p, q: e.target.value }))}
                    placeholder="Начните вводить…"
                  />
                </div>
                {history.loading ? <div className="placeholder">Загрузка истории…</div> : null}
                {history.error ? <div className="form-error">{history.error}</div> : null}
                {!history.loading && !history.error ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Событие</th>
                        <th>Значение</th>
                        <th>Менеджер</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyFiltered.map((h) => (
                        <tr key={h.id}>
                          <td>{fmtDateTime(h.dateTime)}</td>
                          <td>{h.key}</td>
                          <td>{h.value || '—'}</td>
                          <td>{h.managerName || '—'}</td>
                        </tr>
                      ))}
                      {!historyFiltered.length ? (
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                            История пока пустая
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                ) : null}
              </section>
            ) : null}

            {tab === 'marketing' ? (
              <section>
                <div className="form-grid" style={{ marginBottom: 12 }}>
                  <UiInput
                    className="field"
                    label="С даты"
                    type="date"
                    value={marketingFilter.from}
                    onChange={(e) => setMarketingFilter((p) => ({ ...p, from: e.target.value }))}
                  />
                  <UiInput
                    className="field"
                    label="По дату"
                    type="date"
                    value={marketingFilter.to}
                    onChange={(e) => setMarketingFilter((p) => ({ ...p, to: e.target.value }))}
                  />
                  <UiInput
                    className="field"
                    label="Параметр / значение"
                    value={marketingFilter.q}
                    onChange={(e) => setMarketingFilter((p) => ({ ...p, q: e.target.value }))}
                    placeholder="Начните вводить…"
                  />
                </div>
                {marketing.loading ? <div className="placeholder">Загрузка маркетинга…</div> : null}
                {marketing.error ? <div className="form-error">{marketing.error}</div> : null}
                {!marketing.loading && !marketing.error ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Параметр</th>
                        <th>Значение</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketingFiltered.map((row) => (
                        <tr key={row.id}>
                          <td>{fmtDateTime(row.dateTime)}</td>
                          <td>{row.key}</td>
                          <td>{row.value || '—'}</td>
                        </tr>
                      ))}
                      {!marketingFiltered.length ? (
                        <tr>
                          <td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                            Маркетинговых данных пока нет
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>

      {saleModal.open ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeSaleModal();
          }}
        >
          <div className="modal-card clients-page__sale-modal" role="dialog" aria-modal="true" aria-labelledby="sale-modal-title">
            <div className="modal-card__header clients-page__sale-modal-header">
              <h2 id="sale-modal-title">Позиции заказа</h2>
              <button type="button" className="clients-page__icon-btn" aria-label="Закрыть" onClick={closeSaleModal}>
                ×
              </button>
            </div>
            <div className="modal-card__body">
              {saleModal.loading ? <div className="placeholder">Загрузка…</div> : null}
              {saleModal.error ? <div className="form-error">{saleModal.error}</div> : null}
              {!saleModal.loading && !saleModal.error && saleModal.data?.items?.length ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th className="clients-page__col-num">Цена</th>
                      <th>Кол-во</th>
                      <th>Скидка</th>
                      <th className="clients-page__col-num">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saleModal.data.items.map((it) => (
                      <tr key={it.id}>
                        <td>{it.name || '—'}</td>
                        <td className="clients-page__col-num">
                          {it.price != null ? `${Number(it.price).toLocaleString('ru-RU')} ₽` : '—'}
                        </td>
                        <td>{it.count}</td>
                        <td>{fmtSaleItemDiscount(it)}</td>
                        <td className="clients-page__col-num">
                          {Number(it.amount ?? 0).toLocaleString('ru-RU')} ₽
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {!saleModal.loading && !saleModal.error && saleModal.data && !saleModal.data.items?.length ? (
                <div style={{ color: 'var(--color-muted)' }}>Нет позиций</div>
              ) : null}
            </div>
            <div className="modal-card__actions">
              <UiButton type="button" onClick={closeSaleModal}>
                Закрыть
              </UiButton>
            </div>
          </div>
        </div>
      ) : null}

      {paymentCodeModal ? (
        <ModalDialog
          isShow
          size="md"
          type="info"
          onHide={() => setPaymentCodeModal(null)}
        >
          <h2 id="payment-code-title">Код оплаты</h2>
          <p>Передайте этот код родителю. На сайте по нему откроется ссылка на оплату.</p>
          <p style={{ fontSize: 32, letterSpacing: 6, fontWeight: 700, margin: '16px 0', textAlign: 'center' }}>
            {paymentCodeModal}
          </p>
          <div className="modal-card__actions">
            <UiButton
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(paymentCodeModal);
              }}
            >
              Скопировать код
            </UiButton>
            <UiButton type="button" variant="ghost" onClick={() => setPaymentCodeModal(null)}>
              Закрыть
            </UiButton>
          </div>
        </ModalDialog>
      ) : null}
    </section>
  );
}

function ClientContacts({ branchId, clientId, contacts, canManage, onChanged }) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { name: 'email', value: '' } });
  const [error, setError] = useState(null);
  const contactType = watch('name');
  const contactTypeOptions = useMemo(
    () => [
      { value: 'phone', label: 'Телефон' },
      { value: 'email', label: 'Email' },
      { value: 'telegram', label: 'Telegram' },
      { value: 'instagram', label: 'Instagram' },
      { value: 'other', label: 'Другое' },
    ],
    [],
  );

  const onAdd = async (values) => {
    setError(null);
    const payload = { ...values };
    if (values.name === 'phone') {
      const normalized = normalizeRuMobilePhone(values.value);
      if (!normalized) {
        setError('Телефон: формат +7 (9xx) xxx-xx-xx');
        return;
      }
      payload.value = formatRuMobilePhone(normalized);
    }
    try {
      await crmApi(branchId).post(`/clients/${clientId}/contacts`, payload);
      reset({ name: values.name, value: '' });
      await onChanged();
    } catch (err) {
      setError(errMsg(err, 'Не удалось добавить контакт'));
    }
  };

  const onRemove = async (contactId) => {
    setError(null);
    try {
      await crmApi(branchId).delete(`/clients/${clientId}/contacts/${contactId}`);
      await onChanged();
    } catch (err) {
      setError(errMsg(err, 'Не удалось удалить контакт'));
    }
  };

  return (
    <section>
      <h3>Контакты</h3>
      {canManage && (
        <form
          onSubmit={handleSubmit(onAdd)}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}
        >
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <UiSelect
                className="field"
                style={{ minWidth: 180 }}
                label="Тип"
                value={field.value}
                onChange={field.onChange}
                options={contactTypeOptions}
              />
            )}
          />
          <Controller
            name="value"
            control={control}
            rules={{ required: 'Обязательно' }}
            render={({ field }) => (
              contactType === 'phone' ? (
                <label className="ui-field field" style={{ flex: 1 }}>
                  <span className="ui-field__label">Значение</span>
                  <InputMask
                    {...field}
                    className="ui-control"
                    mask={RU_MOBILE_MASK}
                    formatChars={RU_MOBILE_FORMAT_CHARS}
                    maskChar={null}
                    placeholder="+7 (9xx) xxx-xx-xx"
                  />
                  {errors.value && <span className="form-error">{errors.value.message}</span>}
                </label>
              ) : (
                <UiInput
                  {...field}
                  className="field"
                  style={{ flex: 1 }}
                  label="Значение"
                  placeholder="Введите значение"
                  error={errors.value?.message}
                />
              )
            )}
          />
          <UiButton type="submit" disabled={isSubmitting} aria-label="Добавить контакт">
            <svg width="16" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 0C6.58579 0 6.25 0.335786 6.25 0.75V6.25H0.75C0.335786 6.25 0 6.58579 0 7C0 7.41421 0.335786 7.75 0.75 7.75H6.25V13.25C6.25 13.6642 6.58579 14 7 14C7.41421 14 7.75 13.6642 7.75 13.25V7.75H13.25C13.6642 7.75 14 7.41421 14 7C14 6.58579 13.6642 6.25 13.25 6.25H7.75V0.75C7.75 0.335786 7.41421 0 7 0Z"
                fill="currentColor"
              />
            </svg>
          </UiButton>
        </form>
      )}
      {error && <div className="form-error">{error}</div>}
      <ul className="plain-list">
        {contacts.map((c) => (
          <li key={c.id} className="plain-list__item">
            <span>
              <b>{c.name}:</b> {c.value}
            </span>
            {canManage && (
              <UiButton type="button" variant="ghost" size="sm" onClick={() => onRemove(c.id)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
              </UiButton>
            )}
          </li>
        ))}
        {!contacts.length && <li style={{ color: 'var(--color-muted)' }}>Контактов пока нет</li>}
      </ul>
    </section>
  );
}
